/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import { shallowEqual } from 'react-redux'
import styled from '@emotion/styled'
import { BluetoothPairing } from 'models'
import { Organization, trans, TranslationGroup } from 'lib/types'
import { ActionButton, ConditionalRender, LoaderOverlay } from 'components/widgets'
import { useBluetoothScanStream } from 'hooks/bluetoothPairing/useBluetoothScanStream'
import { titleTextStyles } from 'styles'
import { KnownDevicesTable } from './BluetoothPairing/KnownDevicesTable'
import { AddDeviceTypesModal } from './BluetoothPairing/AddDeviceTypesModal'

const StyledContainer = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
})

const SettingsContainer = styled.div({
  position: 'relative',
})

const StyledLinkableText = styled.span({
  fontWeight: 'bold',
  fontSize: '1.2rem',
  color: '#29a096',
})

const TitleRow = styled.div({
  marginBottom: '1.6rem',
})

const ActionRow = styled.div({
  marginBottom: '2.4rem',
  '& button': {
    marginTop: 0,
  },
})

type Props = {
  org: Organization | null
}

export const BluetoothPairingSettings = ({ org }: Props) => {
  const settings: TranslationGroup = trans.settings()
  const knownDevices = BluetoothPairing.use(
    ({ knownByOrgId }) => (org?.id ? knownByOrgId[org.id] || [] : []),
    shallowEqual,
  )
  const loading = BluetoothPairing.use(({ loading }) => loading)
  const { status, error, message, discoveries, connect, disconnect, select } = useBluetoothScanStream(org?.id)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (org?.id) BluetoothPairing.reloadForOrg(org.id)
  }, [org?.id])

  const knownNames = useMemo(() => new Set(knownDevices.map(d => d.name)), [knownDevices])

  // Opening the modal starts the scan; closing it tears the socket back down.
  const openModal = () => {
    setModalOpen(true)
    connect()
  }

  const closeModal = () => {
    setModalOpen(false)
    disconnect()
  }

  const handleAddSelections = (names: string[]) => {
    if (org?.id && names.length) {
      names.forEach(name => BluetoothPairing.addToWhitelist(org.id, { name }))
      select(names) // emit headset:selection for the chosen device types
    }
    closeModal()
  }

  const handleRemove = (id: string) => {
    if (!org?.id) return
    BluetoothPairing.removeFromWhitelist(org.id, id)
  }

  return (
    <SettingsContainer>
      {loading && <LoaderOverlay size={75} />}
      <StyledContainer>
        <TitleRow>
          <h2 css={titleTextStyles}>{settings.bluetooth_pairing}</h2>
          <ConditionalRender condition={!org}>
            <StyledLinkableText>{settings.select_org_bluetooth_pairing}</StyledLinkableText>
          </ConditionalRender>
        </TitleRow>
      </StyledContainer>
      <ConditionalRender condition={!!org}>
        <ActionRow>
          <ActionButton width="180px" height="32px" onClick={openModal} disabled={loading}>
            {settings.add_device_types}
          </ActionButton>
        </ActionRow>
        <KnownDevicesTable devices={knownDevices} onRemove={handleRemove} disabled={loading} />
        <AddDeviceTypesModal
          open={modalOpen}
          status={status}
          message={message}
          error={error}
          discoveries={discoveries}
          knownNames={knownNames}
          onAdd={handleAddSelections}
          onClose={closeModal}
        />
      </ConditionalRender>
    </SettingsContainer>
  )
}
